import React, { useEffect, useImperativeHandle, forwardRef, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import ImageExt from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import mammoth from 'mammoth';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Image as ImageIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';

const VariableNodeView = ({ node }) => {
  return (
    <NodeViewWrapper as="span" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <Chip
        label={`{{${node.attrs.name}}}`}
        size="small"
        color="primary"
        sx={{
          height: 22,
          mx: 0.25,
          fontWeight: 600,
          backgroundColor: '#e3f2fd',
          color: '#1976d2',
          border: '1px solid #1976d2',
          '& .MuiChip-label': { px: 0.75, fontSize: '0.78rem' }
        }}
      />
    </NodeViewWrapper>
  );
};

const Variable = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: { default: '' },
      label: { default: '' }
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { name } = HTMLAttributes;
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-variable': '', 'data-name': name }),
      `{{${name}}}`
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableNodeView);
  }
});

const ResizableImageNodeView = ({ node, updateAttributes, selected, editor }) => {
  const imgRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: node.attrs.width,
    height: node.attrs.height
  });
  const align = node.attrs.align || 'left';

  useEffect(() => {
    setDimensions({ width: node.attrs.width, height: node.attrs.height });
  }, [node.attrs.width, node.attrs.height]);

  const startResize = useCallback((corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;

    const startX = e.clientX;
    const startWidth = img.offsetWidth;
    const startHeight = img.offsetHeight;
    const aspect = startWidth / startHeight;
    const dirX = corner === 'tl' || corner === 'bl' ? -1 : 1;

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) * dirX;
      const newWidth = Math.max(40, startWidth + dx);
      const newHeight = Math.round(newWidth / aspect);
      setDimensions({ width: newWidth, height: newHeight });
    };
    const onUp = (ev) => {
      const dx = (ev.clientX - startX) * dirX;
      const newWidth = Math.max(40, startWidth + dx);
      const newHeight = Math.round(newWidth / aspect);
      updateAttributes({ width: newWidth, height: newHeight });
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [updateAttributes]);

  const setAlign = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateAttributes({ align: value });
  };

  const wrapperAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
  const isEditable = editor?.isEditable;

  const handleStyle = {
    position: 'absolute',
    width: 10,
    height: 10,
    backgroundColor: '#1976d2',
    border: '2px solid white',
    borderRadius: 2,
    zIndex: 2
  };

  return (
    <NodeViewWrapper
      as="div"
      style={{ textAlign: wrapperAlign, margin: '0.5em 0' }}
    >
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          outline: selected ? '2px solid #1976d2' : '2px solid transparent',
          outlineOffset: 2,
          lineHeight: 0
        }}
      >
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt || ''}
          width={dimensions.width || undefined}
          height={dimensions.height || undefined}
          style={{
            maxWidth: '100%',
            height: 'auto',
            display: 'block',
            cursor: isEditable ? 'pointer' : 'default'
          }}
          draggable={false}
        />
        {selected && isEditable && (
          <>
            <div onMouseDown={startResize('tl')} style={{ ...handleStyle, top: -5, left: -5, cursor: 'nwse-resize' }} />
            <div onMouseDown={startResize('tr')} style={{ ...handleStyle, top: -5, right: -5, cursor: 'nesw-resize' }} />
            <div onMouseDown={startResize('bl')} style={{ ...handleStyle, bottom: -5, left: -5, cursor: 'nesw-resize' }} />
            <div onMouseDown={startResize('br')} style={{ ...handleStyle, bottom: -5, right: -5, cursor: 'nwse-resize' }} />
            <div
              contentEditable={false}
              style={{
                position: 'absolute',
                top: -36,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 2,
                background: '#1976d2',
                borderRadius: 4,
                padding: 2,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                zIndex: 3
              }}
            >
              {['left', 'center', 'right'].map((a) => (
                <button
                  key={a}
                  type="button"
                  onMouseDown={setAlign(a)}
                  style={{
                    background: align === a ? 'white' : 'transparent',
                    color: align === a ? '#1976d2' : 'white',
                    border: 'none',
                    borderRadius: 3,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    lineHeight: 1.4
                  }}
                >
                  {a === 'left' ? 'Izq' : a === 'center' ? 'Centro' : 'Der'}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

const ResizableImage = ImageExt.extend({
  name: 'image',
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const w = el.getAttribute('width');
          return w ? parseInt(w, 10) || null : null;
        },
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {})
      },
      height: {
        default: null,
        parseHTML: (el) => {
          const h = el.getAttribute('height');
          return h ? parseInt(h, 10) || null : null;
        },
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {})
      },
      align: {
        default: 'left',
        parseHTML: (el) => el.getAttribute('data-align') || 'left',
        renderHTML: (attrs) => (attrs.align ? { 'data-align': attrs.align } : {})
      }
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  }
});

// Convert plain {{var}} text in HTML into <span data-variable> tags so TipTap parses them as Variable nodes.
const wrapVariableTokens = (html) => {
  if (!html) return '';
  return html.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, name) =>
    `<span data-variable data-name="${name}">{{${name}}}</span>`
  );
};

const TiptapEditor = forwardRef(({ initialContent = '', onChange, onAlert }, ref) => {
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [importing, setImporting] = React.useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ResizableImage.configure({ inline: false, allowBase64: true }),
      Variable
    ],
    content: wrapVariableTokens(initialContent),
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    }
  });

  useImperativeHandle(ref, () => ({
    setContent: (html) => {
      if (editor) editor.commands.setContent(wrapVariableTokens(html || ''));
    },
    insertVariable: (name, label) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent({ type: 'variable', attrs: { name, label: label || name } })
        .insertContent(' ')
        .run();
    },
    getHTML: () => (editor ? editor.getHTML() : ''),
    focus: () => editor?.commands.focus()
  }));

  useEffect(() => {
    return () => editor?.destroy();
  }, [editor]);

  if (!editor) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleWordUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.docx')) {
      onAlert?.('Solo se admiten archivos .docx (Word moderno).', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onAlert?.('El archivo es muy grande. Máximo 10MB.', 'error');
      return;
    }

    try {
      setImporting(true);
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Title'] => h1",
            "p[style-name='Heading 1'] => h1",
            "p[style-name='Heading 2'] => h2",
            "p[style-name='Heading 3'] => h3"
          ]
        }
      );
      editor.commands.setContent(wrapVariableTokens(result.value));
      onChange?.(editor.getHTML());
      if (result.messages?.length) {
        const warns = result.messages.filter(m => m.type === 'warning').length;
        if (warns > 0) {
          onAlert?.(`Documento importado. ${warns} advertencia(s) de formato — revisa el contenido.`, 'warning');
        } else {
          onAlert?.('Documento importado correctamente.', 'success');
        }
      } else {
        onAlert?.('Documento importado correctamente.', 'success');
      }
    } catch (err) {
      console.error('Error importando docx:', err);
      onAlert?.('No se pudo importar el documento.', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      onAlert?.('La imagen es muy grande. Máximo 2MB.', 'error');
      return;
    }
    if (!file.type.startsWith('image/')) {
      onAlert?.('Selecciona un archivo de imagen válido.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      editor.chain().focus().setImage({ src: ev.target.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const isActive = (name, attrs) => editor.isActive(name, attrs);

  const handleAlign = (alignment) => {
    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', { align: alignment }).run();
    } else {
      editor.chain().focus().setTextAlign(alignment).run();
    }
  };

  const alignActive = (alignment) => {
    if (editor.isActive('image')) {
      return editor.getAttributes('image').align === alignment;
    }
    return editor.isActive({ textAlign: alignment });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 0.5,
          mb: 1,
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="primarybutton"
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          sx={{ fontWeight: 600, mr: 1 }}
        >
          {importing ? 'Importando...' : 'Cargar Word'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          style={{ display: 'none' }}
          onChange={handleWordUpload}
        />

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Deshacer">
          <span>
            <IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rehacer">
          <span>
            <IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Negrita">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={isActive('bold') ? 'primary' : 'default'}>
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cursiva">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={isActive('italic') ? 'primary' : 'default'}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Subrayado">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} color={isActive('underline') ? 'primary' : 'default'}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Lista con viñetas">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={isActive('bulletList') ? 'primary' : 'default'}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} color={isActive('orderedList') ? 'primary' : 'default'}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Alinear izquierda">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('left')} color={alignActive('left') ? 'primary' : 'default'}>
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinear centro">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('center')} color={alignActive('center') ? 'primary' : 'default'}>
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinear derecha">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('right')} color={alignActive('right') ? 'primary' : 'default'}>
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Insertar imagen">
          <IconButton size="small" onClick={() => imageInputRef.current?.click()}>
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />

        <Divider orientation="vertical" flexItem />

        <TextField
          select
          size="small"
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1'
              : editor.isActive('heading', { level: 2 }) ? 'h2'
              : editor.isActive('heading', { level: 3 }) ? 'h3'
              : 'p'
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: parseInt(v.replace('h', ''), 10) }).run();
          }}
          SelectProps={{ native: true }}
          sx={{ minWidth: 110, '& .MuiInputBase-root': { height: 32 } }}
        >
          <option value="p">Párrafo</option>
          <option value="h1">Título 1</option>
          <option value="h2">Título 2</option>
          <option value="h3">Título 3</option>
        </TextField>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflowY: 'auto',
          backgroundColor: 'white',
          p: 3,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: '100%',
            fontFamily: 'Arial, sans-serif',
            lineHeight: 1.6,
            fontSize: '14px'
          },
          '& .ProseMirror p': { margin: '0 0 0.75em' },
          '& .ProseMirror h1': { fontSize: '1.6em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror h2': { fontSize: '1.35em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror h3': { fontSize: '1.15em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror img': { maxWidth: '100%', height: 'auto', display: 'block', margin: '0.5em 0' },
          '& .ProseMirror ul, & .ProseMirror ol': { paddingLeft: '1.5em' }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
});

export default TiptapEditor;
